<template>
  <Teleport to="body">
    <div
        v-if="show"
        class="modal-overlay"
        @click.self="$emit('close')"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <font-awesome-icon icon="fa-solid fa-circle-info" class="me-2" />
            Szczegóły rezerwacji
          </h5>
          <button
              type="button"
              class="btn-close"
              :class="{ 'btn-close-white': isDarkMode }"
              @click="$emit('close')"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-warning" role="status">
              <span class="visually-hidden">Ładowanie...</span>
            </div>
            <p class="mt-3 text-muted">Pobieranie szczegółów...</p>
          </div>
          <div v-else-if="error" class="alert alert-danger d-flex align-items-center" role="alert">
            <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="me-2" />
            <div>{{ error }}</div>
          </div>
          <div v-else-if="details" class="accordion-body">
            <div v-if="actionError" class="alert alert-danger mb-3 py-2 small">
              <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="me-1" />
              {{ actionError }}
            </div>

            <!-- Reservation Details Grid matching ReservationsList -->
            <div class="row g-3">
              <div class="row bg-body-tertiary p-3 m-1 rounded">
                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold mb-1">Status</div>
                  <div class="text-body-emphasis d-flex flex-wrap gap-1">
                  <span
                      class="badge"
                      :class="{
                        'bg-success': isConfirmed,
                        'bg-warning text-dark': !isConfirmed && !isCancelled,
                        'bg-danger': isCancelled
                      }"
                  >
                    <font-awesome-icon icon="fa-solid fa-circle-info"/> {{ getStatusText }}
                  </span>
                    <span
                        class="badge"
                        :class="{
                        'bg-success': isPaid,
                        'bg-warning text-dark': !isPaid
                      }"
                    >
                    <font-awesome-icon icon="fa-solid fa-credit-card"/> {{ isPaid ? 'Opłacona' : 'Nieopłacona' }}
                  </span>
                  </div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold mb-1">Typ</div>
                  <div class="text-body-emphasis">
                    <font-awesome-icon :icon="getReservationIcon(getProp('Type', 'type'))" class="me-1 text-warning"/>
                    {{ formatType(getProp('Type', 'type')) || 'Rezerwacja' }}: {{ getProp('TypeName', 'type_name') || spaceName || '-' }}
                  </div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold mb-1">ID Rezerwacji</div>
                  <div class="text-body-emphasis font-monospace text-break small">{{ getProp('Uuid', 'uuid') || reservationId || '-' }}</div>
                </div>
              </div>

              <div class="row bg-body-tertiary p-3 m-1 rounded">
                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold mb-1">Email</div>
                  <div class="text-body-emphasis text-break">{{ getProp('Email', 'email', 'ReservedForEmail', 'reserved_by_email') || '-' }}</div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold mb-1">Telefon</div>
                      <div v-if="isGuest">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            v-model="editForm.contact_phone"
                            placeholder="Numer telefonu"
                        />
                      </div>
                      <div v-else class="text-body-emphasis">{{ getProp('ReservedForPhone', 'ContactPhone') || '-' }}</div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold mb-1">Użytkownik</div>
                  <div class="text-body-emphasis">{{ formatUserType(getProp('ReservedFor')) }}</div>
                </div>
              </div>

              <div class="row bg-body-tertiary p-3 m-1 rounded">
                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold mb-1">Data</div>
                  <div class="text-body-emphasis">{{ formatDate(getProp('StartDate', 'start_date')) }} <small class="text-muted">{{ getDayName(getProp('StartDate', 'start_date')) }}</small></div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold mb-1">Godzina</div>
                  <div class="text-body-emphasis">{{ formatTime(getProp('StartDate')) }} - {{ formatTime(getProp('EndDate')) }}</div>
                </div>
              </div>

              <div class="row bg-body-tertiary p-3 m-1 rounded">
                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold mb-1">Kwota</div>
                  <div class="text-body-emphasis fw-bold text-success">
                    {{ getAmount() }} {{ getCurrency() }}
                  </div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold mb-1">Typ Płatności</div>
                  <div class="text-body-emphasis fw-bold text-success">
                    {{ formatPaymentMethod(getProp('Paid', 'paid')) }}
                  </div>
                </div>
              </div>

              <div class="row bg-body-tertiary p-3 m-1 rounded">
                <div class="col-12">
                  <div class="small text-muted text-uppercase fw-bold mb-1">Notatka</div>
                  <textarea
                      class="form-control form-control-sm mb-2"
                      rows="3"
                      v-model="editForm.note"
                      placeholder="Wpisz notatkę..."
                  ></textarea>
                  <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="saveReservationDetails"
                      :disabled="isSaving"
                  >
                    <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
                    <font-awesome-icon v-else icon="fa-solid fa-floppy-disk" class="me-1" />
                    Zapisz
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-4 text-muted">
            <font-awesome-icon icon="fa-solid fa-magnifying-glass" size="2x" class="mb-2 opacity-50" />
            <p>Brak danych do wyświetlenia</p>
          </div>
        </div>

        <!-- Modal Footer with Reservation List Actions -->
        <div class="modal-footer border-0">
          <div v-if="details && !loading" class="d-flex flex-wrap gap-2 me-auto align-items-center">
            <!-- Inline Action Confirmation -->
            <div v-if="pendingAction" class="alert alert-warning m-0 py-2 px-3 d-flex align-items-center gap-2">
              <small>Czy na pewno chcesz {{ pendingAction.confirmText }}?</small>
              <button
                  type="button"
                  class="btn btn-sm btn-success px-2"
                  @click="executePendingAction"
                  :disabled="isProcessing"
              >
                <span v-if="isProcessing" class="spinner-border spinner-border-sm me-1"></span>
                <font-awesome-icon icon="fa-solid fa-check" /> Tak
              </button>
              <button
                  type="button"
                  class="btn btn-sm btn-danger px-2"
                  @click="pendingAction = null"
                  :disabled="isProcessing"
              >
                <font-awesome-icon icon="fa-solid fa-xmark" /> Nie
              </button>
            </div>

            <template v-else>
              <button
                  v-if="!isConfirmed && !isCancelled"
                  type="button"
                  class="btn btn-sm btn-success"
                  @click="promptAction('confirm', 'zatwierdzić rezerwację')"
                  :disabled="isProcessing"
              >
                <font-awesome-icon icon="fa-solid fa-check" class="me-1"/>
                Zatwierdź
              </button>

              <button
                  v-if="isConfirmed && !isCancelled"
                  type="button"
                  class="btn btn-sm btn-outline-warning"
                  @click="promptAction('unconfirm', 'cofnąć potwierdzenie rezerwacji')"
                  :disabled="isProcessing"
              >
                <font-awesome-icon icon="fa-solid fa-square-minus" class="me-1"/>
                Cofnij potwierdzenie
              </button>

              <button
                  v-if="!isPaid && !isCancelled"
                  type="button"
                  class="btn btn-sm btn-info"
                  @click="promptAction('paid', 'oznaczyć rezerwację jako opłacona')"
                  :disabled="isProcessing"
              >
                <font-awesome-icon icon="fa-solid fa-coins" class="me-1"/>
                Oznacz jako opłacona
              </button>

              <button
                  v-if="isPaid"
                  type="button"
                  class="btn btn-sm btn-outline-info"
                  @click="promptAction('unpaid', 'oznaczyć rezerwację jako nieopłacona')"
                  :disabled="isProcessing"
              >
                <font-awesome-icon icon="fa-solid fa-coins" class="me-1"/>
                Oznacz jako nieopłacona
              </button>

              <button
                  v-if="!isCancelled"
                  type="button"
                  class="btn btn-sm btn-danger"
                  @click="promptAction('cancel', 'anulować rezerwację')"
                  :disabled="isProcessing"
              >
                <font-awesome-icon icon="fa-solid fa-ban" class="me-1"/>
                Anuluj
              </button>

              <button
                  v-if="isCancelled"
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="promptAction('restore', 'przywrócić rezerwację')"
                  :disabled="isProcessing"
              >
                <font-awesome-icon icon="fa-solid fa-reply" class="me-1"/>
                Przywróć
              </button>
            </template>
          </div>

          <button
              type="button"
              class="btn btn-outline-secondary px-4 ms-auto"
              @click="$emit('close')"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { fetchWrapper } from "@/wrappers/api/fetchWrapper";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export default {
  name: 'ReservationDetails',
  components: {
    FontAwesomeIcon
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    reservationId: {
      type: [Number, String],
      default: null
    },
    spaceName: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'reservation-cancelled'],
  data() {
    return {
      details: null,
      loading: false,
      error: null,
      isProcessing: false,
      pendingAction: null,
      actionError: null,
          isSaving: false,
          editForm: {
            contact_phone: '',
            note: ''
          }
    }
  },
  computed: {
    isDarkMode() {
      return document.documentElement.getAttribute('data-bs-theme') === 'dark';
    },
    isGuest() {
      return this.formatUserType(this.getProp('ReservedFor')) === 'Gość';
    },
    isConfirmed() {
      const val = this.getProp('ConfirmedAt', 'confirmed_at', 'confirmed');
      return this.isValidDate(val);
    },
    isCancelled() {
      const val = this.getProp('CanceledAt', 'canceled_at', 'canceled');
      return this.isValidDate(val);
    },
    isPaid() {
      const val = this.getProp('PaidAt', 'paid_at');
      return this.isValidDate(val);
    },
    getStatusText() {
      if (this.isCancelled) return 'Anulowana';
      if (this.isConfirmed) return 'Zatwierdzona';
      return 'Niezatwierdzona';
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.reservationId) {
        this.fetchDetails();
      } else if (!newVal) {
        this.details = null;
        this.error = null;
        this.pendingAction = null;
        this.actionError = null;
            this.editForm = { contact_phone: '', note: '' };
      }
    }
  },
  methods: {
    getProp(...keys) {
      if (!this.details) return null;
      for (const key of keys) {
        if (this.details[key] !== undefined && this.details[key] !== null) {
          return this.details[key];
        }
      }
      return null;
    },
    getAmount() {
      if (!this.details) return 0;
      const amt = this.getProp('Amount', 'amount');
      if (amt && typeof amt === 'object' && amt.Valid !== undefined) {
        return amt.Valid ? amt.Float64 : 0;
      }
      return amt ?? 0;
    },
    getCurrency() {
      if (!this.details) return '';
      const curr = this.getProp('Currency', 'currency');
      if (curr && typeof curr === 'object' && curr.String !== undefined) {
        return curr.String;
      }
      return curr || '';
    },
    getSpaces() {
      const spaces = this.getProp('Spaces', 'spaces');
      if (Array.isArray(spaces) && spaces.length > 0) return spaces;
      const singleSpace = this.spaceName || this.getProp('space_id', 'SpaceId');
      if (singleSpace) return [{ Name: singleSpace }];
      return [];
    },
    isValidDate(dateStr) {
      if (!dateStr) return false;
      if (typeof dateStr === 'object' && dateStr.Valid !== undefined) return dateStr.Valid;
      if (typeof dateStr === 'string' && dateStr.startsWith('0001-01-01')) return false;
      const d = new Date(dateStr);
      return !isNaN(d.getTime()) && d.getFullYear() > 1;
    },
    formatDateTime(dateStr) {
      if (!dateStr || !this.isValidDate(dateStr)) return '-';
      const d = new Date(dateStr);
      return `${d.toLocaleDateString('pl-PL')} ${d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pl-PL')
    },
    formatTime(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return isNaN(d.getTime()) ? '-' : d.toLocaleTimeString('pl-PL', {hour: '2-digit', minute: '2-digit'})
    },
    getDayName(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pl-PL', {weekday: 'long'})
    },
    getReservationIcon(typeStr) {
      return (typeStr && String(typeStr).toLowerCase() === 'event') ? 'fa-solid fa-bullhorn' : 'fa-solid fa-bullseye';
    },
    formatType(typeStr) {
      return (typeStr && String(typeStr).toLowerCase() === 'event') ? 'Wydarzenie' : 'Stanowisko';
    },
    formatPaymentMethod(method) {
      if (method === null || method === undefined || method === '') return '-';
      const translations = {
        'pay_at_location': 'Płatność na miejscu',
        'p24': 'Przelewy24 (Online)'
      };
      return translations[method] || method;
    },
    formatUserType(reservedFor) {
      if (reservedFor === null || reservedFor === undefined || reservedFor === 0) return 'Gość';
      return 'Zarejestrowany';
    },
    promptAction(type, confirmText) {
      this.pendingAction = { type, confirmText };
    },
    async saveReservationDetails() {
      if (!this.details) return;

      this.isSaving = true;
      this.actionError = null;

      const payload = {
        uuid: this.getProp('Uuid', 'uuid') || this.reservationId,
        contact_phone: this.editForm.contact_phone || '',
        note: this.editForm.note || ''
      };

      try {
        await fetchWrapper.post('/admin/reservations/space/update', payload);
        this.$emit('reservation-cancelled');
        await this.fetchDetails();
      } catch (err) {
        this.actionError = typeof err === 'string' ? err : (err.message || 'Nie udało się zapisać zmian');
      } finally {
        this.isSaving = false;
      }
    },
    async executePendingAction() {
      if (!this.pendingAction || !this.reservationId) return;

      this.isProcessing = true;
      this.actionError = null;

      const endpointMap = {
        confirm: '/admin/reservations/confirm',
        unconfirm: '/admin/reservations/unconfirm',
        paid: '/admin/reservations/paid',
        unpaid: '/admin/reservations/unpaid',
        cancel: '/admin/reservations/cancel',
        restore: '/admin/reservations/restore-cancelled',
      };

      const endpoint = endpointMap[this.pendingAction.type];

      try {
        await fetchWrapper.post(endpoint, { uuid: this.reservationId });
        this.pendingAction = null;
        this.$emit('reservation-cancelled'); // notify parent to refresh reservations list/view
        await this.fetchDetails();
      } catch (err) {
        this.actionError = typeof err === 'string' ? err : (err.message || 'Wystąpił błąd');
      } finally {
        this.isProcessing = false;
      }
    },
    async fetchDetails() {
      if (!this.reservationId) {
        this.error = 'Brak ID rezerwacji';
        return;
      }

      this.loading = true;
      this.error = null;
      this.details = null;
      this.pendingAction = null;
      this.actionError = null;

      try {
        const response = await fetchWrapper.get(`/admin/reservations/${this.reservationId}`);
        this.details = response.reservationDetails || response;
            this.editForm.contact_phone = this.getProp('ReservedForPhone', 'ContactPhone', 'contact_phone') || '';
            this.editForm.note = this.getProp('Note', 'note', 'Description', 'description') || '';
      } catch (err) {
        this.error = err || 'Nie udało się pobrać szczegółów rezerwacji';
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-content {
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 1rem;
  max-width: 650px;
  width: 100%;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--bs-border-color);
  background-color: var(--bs-tertiary-bg);
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #6596d4;
  margin: 0;
  display: flex;
  align-items: center;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 1rem 1.5rem;
}

/* Light theme adjustments */
[data-bs-theme="light"] .modal-content {
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
}

/* Responsive */
@media (max-width: 576px) {
  .modal-content {
    border-radius: 0.75rem;
  }

  .modal-header, .modal-body, .modal-footer {
    padding: 1rem;
  }
}
</style>